import React, { useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Menu, makeStyles, ClickAwayListener } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { EditMessageContext } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForwardModal from "../../components/ForwardMessageModal";
import ShowTicketOpen from "../ShowTicketOpenModal";
import AcceptTicketWithoutQueue from "../AcceptTicketWithoutQueueModal";
import toastError from "../../errors/toastError";

const QUICK_REACTIONS = ["❤️", "👍", "😂", "😮", "😢", "🙏", "👏", "🔥"];

const useStyles = makeStyles((theme) => ({
  reactionBar: {
    display: "flex",
    padding: "6px 8px",
    gap: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: "center",
  },
  reactionBtn: {
    background: "none",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    padding: "2px 4px",
    borderRadius: 6,
    transition: "transform 0.12s, background 0.12s",
    lineHeight: 1,
    "&:hover": {
      background: theme.palette.action.hover,
      transform: "scale(1.3)",
    },
  },
  moreBtnWrapper: {
    marginLeft: "auto",
  },
  moreBtn: {
    background: "none",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "50%",
    width: 30,
    height: 30,
    cursor: "pointer",
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  pickerWrapper: {
    position: "fixed",
    zIndex: 9999,
  },
}));

const MessageOptionsMenu = ({
  message,
  menuOpen,
  handleClose,
  anchorEl,
  isGroup,
  queueId,
  whatsappId
}) => {
  const classes = useStyles();
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  const moreBtnRef = useRef(null);
  const { user } = useContext(AuthContext);
  const editingContext = useContext(EditMessageContext);
  const setEditingMessage = editingContext ? editingContext.setEditingMessage : null;
  const { setTabOpen } = useContext(TicketsContext);
  const history = useHistory();

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);

  const { showSelectMessageCheckbox,
    setShowSelectMessageCheckbox,
    selectedMessages,
    forwardMessageModalOpen,
    setForwardMessageModalOpen } = useContext(ForwardMessageContext);

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user?.id,
        status: "open",
        queueId: queueId,
        whatsappId: whatsappId
      });
      setTicketOpen(ticket);
      if (ticket.queueId === null) {
        setAcceptTicketWithouSelectQueueOpen(true);
      } else {
        setTabOpen("open");
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      const ticket = JSON.parse(err.response.data.error);
      if (ticket.userId !== user?.id) {
        setOpenAlert(true);
        setUserTicketOpen(ticket.user.name);
        setQueueTicketOpen(ticket.queue.name);
      } else {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        setTabOpen(ticket.status);
        history.push(`/tickets/${ticket.uuid}`);
      }
    }
    handleClose();
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSetShowSelectCheckbox = () => {
    setShowSelectMessageCheckbox(!showSelectMessageCheckbox);
    handleClose();
  };

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleEditMessage = async () => {
    setEditingMessage(message);
    handleClose();
  };

  const hanldeReplyMessage = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const isWithinFifteenMinutes = () => {
    const fifteenMinutesInMilliseconds = 15 * 60 * 1000;
    const currentTime = new Date();
    const messageTime = new Date(message.createdAt);
    return currentTime - messageTime <= fifteenMinutesInMilliseconds;
  };

  const handleReactToMessage = async (emoji) => {
    try {
      await api.post(`/messages/${message.id}/react`, { emoji });
    } catch (err) {
      toastError(err);
    }
    setShowPicker(false);
    handleClose();
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const handleOpenPicker = () => {
    if (moreBtnRef.current) {
      const rect = moreBtnRef.current.getBoundingClientRect();
      const pickerWidth = 338;
      const pickerHeight = 430;
      let left = rect.left;
      let top = rect.bottom + 4;

      if (left + pickerWidth > window.innerWidth) {
        left = window.innerWidth - pickerWidth - 8;
      }
      if (top + pickerHeight > window.innerHeight) {
        top = rect.top - pickerHeight - 4;
      }
      setPickerPos({ top, left });
    }
    setShowPicker(true);
  };

  return (
    <>
      <AcceptTicketWithoutQueue
        modalOpen={acceptTicketWithouSelectQueueOpen}
        onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
        ticket={ticketOpen}
        ticketId={ticketOpen?.id}
      />
      <ShowTicketOpen
        isOpen={openAlert}
        handleClose={handleCloseAlert}
        user={userTicketOpen}
        queue={queueTicketOpen}
      />
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>

      <ForwardModal
        modalOpen={forwardMessageModalOpen}
        messages={selectedMessages}
        onClose={() => {
          setForwardMessageModalOpen(false);
          setShowSelectMessageCheckbox(false);
        }}
      />

      {showPicker && (
        <ClickAwayListener onClickAway={() => setShowPicker(false)}>
          <div
            className={classes.pickerWrapper}
            style={{ top: pickerPos.top, left: pickerPos.left }}
          >
            <Picker
              onSelect={(emoji) => handleReactToMessage(emoji.native)}
              showPreview={false}
              showSkinTones={false}
              perLine={8}
              i18n={{
                search: "Buscar",
                categories: {
                  search: "Resultados",
                  recent: "Recentes",
                  smileys: "Rostos",
                  people: "Pessoas",
                  nature: "Natureza",
                  foods: "Comida",
                  activity: "Atividade",
                  places: "Lugares",
                  objects: "Objetos",
                  symbols: "Símbolos",
                  flags: "Bandeiras",
                  custom: "Customizado",
                },
              }}
            />
          </div>
        </ClickAwayListener>
      )}

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={menuOpen}
        onClose={() => { setShowPicker(false); handleClose(); }}
      >
        <div className={classes.reactionBar}>
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className={classes.reactionBtn}
              onClick={() => handleReactToMessage(emoji)}
              title={emoji}
            >
              {emoji}
            </button>
          ))}
          <span className={classes.moreBtnWrapper}>
            <button
              ref={moreBtnRef}
              className={classes.moreBtn}
              onClick={handleOpenPicker}
              title="Mais emojis"
            >
              +
            </button>
          </span>
        </div>
        {message.fromMe && (
          <MenuItem key="delete" onClick={handleOpenConfirmationModal}>
            {i18n.t("messageOptionsMenu.delete")}
          </MenuItem>
        )}
        {message.fromMe && isWithinFifteenMinutes() && (
          <MenuItem key="edit" onClick={handleEditMessage}>
            {i18n.t("messageOptionsMenu.edit")}
          </MenuItem>
        )}
        <MenuItem onClick={hanldeReplyMessage}>
          {i18n.t("messageOptionsMenu.reply")}
        </MenuItem>
        <MenuItem onClick={handleSetShowSelectCheckbox}>
          {i18n.t("messageOptionsMenu.forward")}
        </MenuItem>
        {!message.fromMe && isGroup && (
          <MenuItem onClick={() => handleSaveTicket(message?.contact?.id)}>
            {i18n.t("messageOptionsMenu.talkTo")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
